import { Injectable, inject } from '@angular/core';
import { SecurityLockService } from '../auth/security-lock.service';
import { SecurityEventService } from './security-event.service';

interface StorageEntry {
  key: string;
  value: string;
}

interface EncryptedStoragePayload {
  schemaVersion: 1;
  algorithm: 'AES-GCM';
  iv: string;
  data: string;
}

interface CryptoKeyEntry {
  id: string;
  key: CryptoKey;
}

@Injectable({ providedIn: 'root' })
export class IndexedDbStorageService {
  private readonly securityLock = inject(SecurityLockService);
  private readonly securityEvents = inject(SecurityEventService);
  private readonly databaseName = 'tops-offline-storage';
  private readonly storeName = 'entries';
  private readonly keyStoreName = 'cryptoKeys';
  private readonly dataEncryptionKeyId = 'tops-offline-data-key-v1';
  private readonly cache = new Map<string, string>();
  private readonly tamperedKeys = new Set<string>();
  private readonly pendingWrites = new Set<Promise<void>>();
  private readonly databasePromise = this.openDatabase();
  private readonly encryptionKeyPromise = this.loadOrCreateEncryptionKey();
  private readonly readyPromise = this.initialize();

  ready(): Promise<void> {
    return this.readyPromise;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  getItem(key: string): string | null {
    return this.cache.has(key) ? this.cache.get(key) ?? null : null;
  }

  setItem(key: string, value: string): void {
    this.cache.set(key, value);
    this.trackWrite(this.writeEntry(key, value));
  }

  removeItem(key: string): void {
    this.cache.delete(key);
    this.trackWrite(this.deleteEntry(key));
  }

  async validateIntegrity(): Promise<boolean> {
    await this.ready();
    await Promise.allSettled(Array.from(this.pendingWrites));

    const database = await this.databasePromise;
    if (!database) return !this.hasTamperingDetected();

    const entries = await this.readEntries(database);
    const storedKeys = new Set<string>();

    for (const entry of entries) {
      if (!entry?.key) continue;

      storedKeys.add(entry.key);
      const encryptedPayload = this.encryptedPayload(entry.value);
      if (!encryptedPayload) {
        this.markTampered(entry.key, 'stored-value-is-not-encrypted');
        continue;
      }

      const decryptedValue = await this.decryptStoredValue(entry.key, entry.value);
      if (this.tamperedKeys.has(entry.key)) continue;

      if (!this.cache.has(entry.key) || this.cache.get(entry.key) !== decryptedValue) {
        this.markTampered(entry.key, 'stored-value-changed-outside-application');
      }
    }

    Array.from(this.cache.keys()).forEach((key) => {
      if (!storedKeys.has(key)) this.markTampered(key, 'stored-entry-removed-outside-application');
    });

    return !this.hasTamperingDetected();
  }

  hasTamperingDetected(): boolean {
    return this.tamperedKeys.size > 0 || this.securityLock.currentLock?.reason === 'OFFLINE_DATA_TAMPER_DETECTED';
  }

  tamperingDetails(): Record<string, unknown> {
    return {
      tamperedKeys: Array.from(this.tamperedKeys),
      lock: this.securityLock.currentLock,
    };
  }

  private async initialize(): Promise<void> {
    const database = await this.databasePromise;
    if (!database) return;

    await this.loadEntries(database);
    await this.migrateLocalStorage(database);
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null);

    return new Promise((resolve) => {
      const request = indexedDB.open(this.databaseName, 2);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(this.storeName)) {
          database.createObjectStore(this.storeName, { keyPath: 'key' });
        }
        if (!database.objectStoreNames.contains(this.keyStoreName)) {
          database.createObjectStore(this.keyStoreName, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
  }

  private async loadEntries(database: IDBDatabase): Promise<void> {
    const entries = await this.readEntries(database);

    for (const entry of entries) {
      if (!entry?.key) continue;

      const encryptedPayload = this.encryptedPayload(entry.value);
      const value = encryptedPayload ? await this.decryptStoredValue(entry.key, entry.value) : entry.value;
      this.cache.set(entry.key, value);
      if (!encryptedPayload) this.trackWrite(this.writeEntry(entry.key, value));
    }
  }

  private readEntries(database: IDBDatabase): Promise<StorageEntry[]> {
    return new Promise<StorageEntry[]>((resolve) => {
      const transaction = database.transaction(this.storeName, 'readonly');
      const request = transaction.objectStore(this.storeName).getAll();
      let entries: StorageEntry[] = [];

      request.onsuccess = () => {
        entries = Array.isArray(request.result) ? request.result as StorageEntry[] : [];
      };

      transaction.oncomplete = () => resolve(entries);
      transaction.onerror = () => resolve([]);
      transaction.onabort = () => resolve([]);
    });
  }

  private trackWrite(write: Promise<void>): void {
    this.pendingWrites.add(write);
    void write.finally(() => this.pendingWrites.delete(write));
  }

  private async migrateLocalStorage(database: IDBDatabase): Promise<void> {
    const entries = this.localStorageEntries();
    if (!entries.length) return;

    const encryptedEntries = await Promise.all(entries.map(async (entry) => ({
      key: entry.key,
      value: await this.encryptStoredValue(entry.value),
    })));

    return new Promise((resolve) => {
      const transaction = database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      entries.forEach((entry) => this.cache.set(entry.key, entry.value));
      encryptedEntries.forEach((entry) => {
        store.put(entry);
      });

      transaction.oncomplete = () => {
        this.removeMigratedLocalStorage(entries.map((entry) => entry.key));
        resolve();
      };
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
  }

  private async writeEntry(key: string, value: string): Promise<void> {
    const encryptedValue = await this.encryptStoredValue(value);

    return this.databasePromise.then((database) => {
      if (!database) return;

      return new Promise<void>((resolve) => {
        const transaction = database.transaction(this.storeName, 'readwrite');
        transaction.objectStore(this.storeName).put({ key, value: encryptedValue });
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      });
    });
  }

  private deleteEntry(key: string): Promise<void> {
    return this.databasePromise.then((database) => {
      if (!database) return;

      return new Promise<void>((resolve) => {
        const transaction = database.transaction(this.storeName, 'readwrite');
        transaction.objectStore(this.storeName).delete(key);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => resolve();
        transaction.onabort = () => resolve();
      });
    });
  }

  private localStorageEntries(): StorageEntry[] {
    try {
      return Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
        .filter((key): key is string => !!key)
        .map((key) => ({ key, value: localStorage.getItem(key) ?? '' }));
    } catch {
      return [];
    }
  }

  private removeMigratedLocalStorage(keys: string[]): void {
    try {
      keys.forEach((key) => localStorage.removeItem(key));
    } catch {}
  }

  private async loadOrCreateEncryptionKey(): Promise<CryptoKey | null> {
    if (!crypto?.subtle) return null;

    const database = await this.databasePromise;
    if (!database || !database.objectStoreNames.contains(this.keyStoreName)) return null;

    const existingKey = await new Promise<CryptoKey | null>((resolve) => {
      const transaction = database.transaction(this.keyStoreName, 'readonly');
      const request = transaction.objectStore(this.keyStoreName).get(this.dataEncryptionKeyId);

      request.onsuccess = () => {
        const entry = request.result as CryptoKeyEntry | undefined;
        resolve(entry?.key ?? null);
      };
      request.onerror = () => resolve(null);
      transaction.onerror = () => resolve(null);
      transaction.onabort = () => resolve(null);
    });

    if (existingKey) return existingKey;

    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    await new Promise<void>((resolve) => {
      const transaction = database.transaction(this.keyStoreName, 'readwrite');
      transaction.objectStore(this.keyStoreName).put({ id: this.dataEncryptionKeyId, key });
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });
    return key;
  }

  private async encryptStoredValue(value: string): Promise<string> {
    const key = await this.encryptionKeyPromise;
    if (!key) return value;

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encodedValue = new TextEncoder().encode(value);
    const encryptedBytes = new Uint8Array(await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: this.bytesToArrayBuffer(iv) },
      key,
      this.bytesToArrayBuffer(encodedValue),
    ));
    const payload: EncryptedStoragePayload = {
      schemaVersion: 1,
      algorithm: 'AES-GCM',
      iv: this.bytesToBase64(iv),
      data: this.bytesToBase64(encryptedBytes),
    };

    return JSON.stringify(payload);
  }

  private async decryptStoredValue(storageKey: string, value: string): Promise<string> {
    const payload = this.encryptedPayload(value);
    if (!payload) return value;

    const key = await this.encryptionKeyPromise;
    if (!key) return '';

    try {
      const iv = this.bytesToArrayBuffer(this.base64ToBytes(payload.iv));
      const data = this.bytesToArrayBuffer(this.base64ToBytes(payload.data));
      const decryptedBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
      return new TextDecoder().decode(decryptedBytes);
    } catch {
      this.markTampered(storageKey, 'encrypted-value-decryption-failed');
      return '';
    }
  }

  private markTampered(storageKey: string, reason: string): void {
    this.tamperedKeys.add(storageKey);
    const lock = this.securityLock.lockApp(
      'OFFLINE_DATA_TAMPER_DETECTED',
      'Offline data integrity check failed. Re-authentication is required.',
      { storageKey, reason },
    );
    this.securityEvents.record('OFFLINE_DATA_TAMPER_DETECTED', {
      storageKey,
      reason,
      lock,
    });
  }

  private encryptedPayload(value: string): EncryptedStoragePayload | null {
    try {
      const payload = JSON.parse(value) as EncryptedStoragePayload;
      return payload?.schemaVersion === 1 && payload.algorithm === 'AES-GCM' && !!payload.iv && !!payload.data
        ? payload
        : null;
    } catch {
      return null;
    }
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => binary += String.fromCharCode(byte));
    return btoa(binary);
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = atob(value);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  }

  private bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }

}